import Vue from 'vue';
import axios from 'axios';
import { apiDomain } from '@/config';
import applications from './applications';
import employees from './employees';
import jobs from './jobs';
import profile from './profile';

// TYPES
const RESET = 'RESET';

const FETCH_INITIAL_PENDING = 'FETCH_INITIAL_PENDING';
const FETCH_INITIAL_FULFILLED = 'FETCH_INITIAL_FULFILLED';
const FETCH_INITIAL_ERROR = 'FETCH_INITIAL_ERROR';

const FETCH_COMPANIES_PENDING = 'FETCH_COMPANIES_PENDING';
const FETCH_COMPANIES_FULFILLED = 'FETCH_COMPANIES_FULFILLED';
const FETCH_COMPANIES_ERROR = 'FETCH_COMPANIES_ERROR';

const FETCH_COMPANY_INITIAL_PENDING = 'FETCH_COMPANY_INITIAL_PENDING';
const FETCH_COMPANY_INITIAL_FULFILLED = 'FETCH_COMPANY_INITIAL_FULFILLED';
const FETCH_COMPANY_INITIAL_ERROR = 'FETCH_COMPANY_INITIAL_ERROR';

const FETCH_DASHBOARD_PENDING = 'FETCH_DASHBOARD_PENDING';
const FETCH_DASHBOARD_FULFILLED = 'FETCH_DASHBOARD_FULFILLED';
const FETCH_DASHBOARD_ERROR = 'FETCH_DASHBOARD_ERROR';

const FETCH_COOPERATION_AGREEMENTS_PENDING = 'FETCH_COOPERATION_AGREEMENTS_PENDING';
const FETCH_COOPERATION_AGREEMENTS_FULFILLED = 'FETCH_COOPERATION_AGREEMENTS_FULFILLED';
const FETCH_COOPERATION_AGREEMENTS_ERROR = 'FETCH_COOPERATION_AGREEMENTS_ERROR';

export default {
  namespaced: true,
  state: {
    fetchInitialPending: false,
    fetchCompaniesPending: false,
    companies: [],
    fetchCompanyInitialPending: false,
    fetchDashboardPending: false,
    fetchCooperationAgreementsPending: false,
    cooperationAgreements: [],
    companySizes: ['micro', 'small', 'medium', 'big'],
  },
  mutations: {
    [RESET](state) {
      state.companies = [];
      state.cooperationAgreements = [];
    },
    // FETCH_INITIAL
    [FETCH_INITIAL_PENDING](state) {
      state.fetchInitialPending = true;
    },
    [FETCH_INITIAL_FULFILLED](state) {
      state.fetchInitialPending = false;
    },
    [FETCH_INITIAL_ERROR](state) {
      state.fetchInitialPending = false;
    },
    // FETCH_COMPANIES
    [FETCH_COMPANIES_PENDING](state) {
      state.fetchCompaniesPending = true;
    },
    [FETCH_COMPANIES_FULFILLED](state, data) {
      state.companies = data;
      state.fetchCompaniesPending = false;
    },
    [FETCH_COMPANIES_ERROR](state) {
      state.fetchCompaniesPending = false;
    },
    // FETCH_COMPANY_INITIAL
    [FETCH_COMPANY_INITIAL_PENDING](state) {
      state.fetchCompanyInitialPending = true;
    },
    [FETCH_COMPANY_INITIAL_FULFILLED](state) {
      state.fetchCompanyInitialPending = false;
    },
    [FETCH_COMPANY_INITIAL_ERROR](state) {
      state.fetchCompanyInitialPending = false;
    },
    // FETCH_DASHBOARD
    [FETCH_DASHBOARD_PENDING](state) {
      state.fetchDashboardPending = true;
    },
    [FETCH_DASHBOARD_FULFILLED](state) {
      state.fetchDashboardPending = false;
    },
    [FETCH_DASHBOARD_ERROR](state) {
      state.fetchDashboardPending = false;
    },
    // FETCH_COOPERATION_AGREEMENTS
    [FETCH_COOPERATION_AGREEMENTS_PENDING](state) {
      state.fetchCooperationAgreementsPending = true;
    },
    [FETCH_COOPERATION_AGREEMENTS_FULFILLED](state, { data }) {
      state.cooperationAgreements = data.data;
      state.fetchCooperationAgreementsPending = true;
    },
    [FETCH_COOPERATION_AGREEMENTS_ERROR](state) {
      state.fetchCooperationAgreementsPending = true;
    },
  },
  actions: {
    reset: ({ commit, dispatch }) => {
      commit('RESET');
      dispatch('company/profile/reset', null, { root: true });
      dispatch('company/jobs/reset', null, { root: true });
      dispatch('company/employees/reset', null, { root: true });
      dispatch('company/applications/reset', null, { root: true });
    },
    setSelectedCompanyId: ({ commit }, companyId) => {
      commit('SET_SELECTED_COMPANY_ID', companyId);
    },
    setCompanies: ({ commit }, companies) => {
      if (companies.length > 0) {
        commit('FETCH_COMPANIES_FULFILLED', companies);
      } else {
        Vue.router.push({ name: 'add.company' });
      }
    },
    async fetchInitial({ commit, dispatch }, companies) {
      commit('FETCH_INITIAL_PENDING');
      try {
        dispatch('general/fetchAvamOccupationClassifications', null, { root: true });
        if (companies.length === 0) {
          Vue.router.push({ name: 'add.company' });
        } else {
          commit('FETCH_COMPANIES_FULFILLED', companies);
          Vue.router.push({ name: 'company.dashboard', params: { companyId: companies[0].id } });
        }
        commit('FETCH_INITIAL_FULFILLED');
        return Promise.resolve(true);
      } catch (error) {
        commit('FETCH_INITIAL_ERROR');
        return Promise.reject(error);
      }
    },
    async fetchCompanyInitial({ commit, dispatch }, id) {
      commit('FETCH_COMPANY_INITIAL_PENDING');
      try {
        await Promise.all([
          dispatch('company/profile/fetchCompany', id, { root: true }),
          dispatch('company/profile/checkCompanyProfile', { companyId: id, forceCheck: true }, { root: true }),
          dispatch('user/fetchNotifications', id, { root: true }),
        ]);

        commit('FETCH_COMPANY_INITIAL_FULFILLED');
        return true;
      } catch (error) {
        commit('FETCH_COMPANY_INITIAL_ERROR');
        return Promise.reject(error);
      }
    },
    async fetchCompanies({ commit }) {
      commit('FETCH_COMPANIES_PENDING');
      try {
        const response = await axios.get(`${apiDomain}companies`);
        commit('FETCH_COMPANIES_FULFILLED', response.data.data);
        return Promise.resolve(response.data.data);
      } catch (error) {
        commit('FETCH_COMPANIES_ERROR');
        return Promise.reject(error);
      }
    },
    async fetchDashboard({ commit, dispatch }, id) {
      commit('FETCH_DASHBOARD_PENDING');
      try {
        const application = {
          companyId: id,
          listType: 'open',
        };
        await Promise.all([
          dispatch('company/applications/fetchApplications', application, { root: true }),
          dispatch('company/jobs/fetchJobs', { companyId: id, listType: 'active' }, { root: true }),
          dispatch('company/employees/fetchEmployeesReEnter', id, { root: true }),
          dispatch('company/employees/fetchEmployeesAssignment', id, { root: true }),
          dispatch('fetchCooperationAgreements', id),
        ]);
        commit('FETCH_DASHBOARD_FULFILLED');
        return true;
      } catch (error) {
        commit('FETCH_DASHBOARD_ERROR');
        return Promise.reject(error);
      }
    },
    async fetchCooperationAgreements({ commit }, id) {
      commit('FETCH_COOPERATION_AGREEMENTS_PENDING');
      try {
        const response = await axios.get(`${apiDomain}company/${id}/cooperation_agreements`);
        commit('FETCH_COOPERATION_AGREEMENTS_FULFILLED', response);
        return true;
      } catch (error) {
        commit('FETCH_COOPERATION_AGREEMENTS_ERROR');
        return Promise.reject(error);
      }
    },
  },
  modules: {
    profile,
    jobs,
    applications,
    employees,
  },
};
